import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { getDNAPaymentScript, getPublicConfigurationValue } from '../../utils/Payment';
import Spinner from '../../layout/spinner';
import { companyNameReplace } from '../../utils';
import { t } from 'i18next';
import { DNAPaymentsType } from '../../models/payment';
import { Common } from '../../utils/constants';
import { env } from 'config/env';
declare global {
  interface Window {
    DNAPayments: DNAPaymentsType;
  }
}

function getPaymentData(
  access_token: string,
  accountId: string,
  invoiceId: string,
  terminal: string,
  amount: number,
  currency: string,
  language: string,
  transactionType: string,
  donationAmount: number,
) {
  return {
    paymentSettings: {
      terminalId: terminal,
      returnUrl: `${window.location.origin}${window.location.pathname}?success=true&iid=${invoiceId}`,
      failureReturnUrl: `${window.location.origin}${window.location.pathname}?success=false&iid=${invoiceId}`,
      callbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/driver/payment/dna-webhook/${invoiceId}`,
      failureCallbackUrl: `${env.VITE_API_SERVER}/${env.VITE_API_VERSION}/driver/payment/dna-webhook/${invoiceId}`,
      cscMatrix: 22,
      paMatrix: 3,
    },
    customerDetails: {
      accountDetails: {
        accountId: accountId,
      },
    },
    periodic: {
      periodicType: 'ucof',
    },
    invoiceId,
    amount,
    auth: { access_token: access_token },
    description: companyNameReplace(t('Payment.description')),
    language: language || 'en',
    currency,
    transactionType,
    donationAmount,
  };
}

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (location && location.search.includes('success=false&redirect=true')) {
      navigate(`/payment${location.search}`);
    } else if (location && location.search.includes('success=false')) {
      navigate(`/payment${location.search}`);
    }

    const params = Object.fromEntries(searchParams.entries());
    const rawTestMode = params.isTestMode;
    const isTestMode = !!(rawTestMode === undefined || rawTestMode === null || rawTestMode === 'true');

    document.getElementById('loading')?.classList.remove('spinner-disable');
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = getDNAPaymentScript(isTestMode);
    script.nonce = 'ils+vsV+OAN1z2QUGBzoyQ==';
    script.async = true;
    script.onload = () => openPaymentPage(params, isTestMode);
    script.onerror = () => {
      navigate(
        `${location.pathname}?success=false&errorcode=payment_script_load_fail&message=unable%20to%20load%20payment%20script`,
        { replace: true },
      );
    };
    if ((params.success || '').length === 0) {
      document.body.appendChild(script);
    }
  }, [location, navigate]);

  return (
    <>
      <div>
        <Spinner />
      </div>
    </>
  );
};

const openPaymentPage = async (params, isTestMode: boolean) => {
  const version = params?.version || null;
  const platform = params?.platform || null;
  const enable_google_apple_pay = await getPublicConfigurationValue(
    Common.ConfigurationKey.ENABLE_GOOGLE_PAY_APPLE_PAY_DNA,
  );
  const shouldEnableGoogleApplePay = enable_google_apple_pay === 'true' && (platform !== null || version !== null);
  const isEnableDonation = params.isEnableDonation === 'true';
  const donationAmount = params.donationAmount ? Number(params.donationAmount) : 0;
  const config = {
    autoRedirectDelayInMs: 10000,
    scopes: {
      allowHosted: true,
    },
    paymentMethods: [
      {
        name: window.DNAPayments.paymentMethods.BankCard,
      },
      ...(shouldEnableGoogleApplePay
        ? [
            {
              name: window.DNAPayments.paymentMethods.GooglePay,
            },
            {
              name: window.DNAPayments.paymentMethods.ApplePay,
            },
          ]
        : []),
    ],
    isTestMode: isTestMode,
    paymentTimeoutInSeconds: 900,
    isEnableDonation,
    locale: {
      targetLocale: params.targetLocale || 'en_GB',
    },
  };

  window.DNAPayments.configure(config);

  window.DNAPayments.openPaymentPage(
    getPaymentData(
      params.accessToken,
      params.accountId,
      params.invoiceId,
      params.terminal,
      +params.amount,
      params.currency,
      params.language,
      params.transactionType,
      donationAmount,
    ),
  );
};

export default PaymentPage;
